import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService, SharedModule } from 'primeng/api';
import { Button } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import { debounceTime, Subject } from 'rxjs';
import SampleHardwareRevision from '../../../../types/sample-hardware-revision/SampleHardwareRevision';
import SampleHardwareRevisionWriteDTO from '../../../../types/sample-hardware-revision/SampleHardwareRevisionWriteDTO';
import Page from '../../../../types/shared/Page';
import { IsAdminDirective } from '../../directives/is-admin.directive';
import { SampleHardwareRevisionService } from '../../services/sample-hardware-revision.service';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';
import {
    CreateSampleHardwareRevisionDialogComponent
} from '../shared/sample-hardware/create-sample-hardware-revision-dialog/create-sample-hardware-revision-dialog.component';
import {
    UpdateSampleHardwareRevisionDialogComponent
} from '../shared/sample-hardware/update-sample-hardware-revision-dialog/update-sample-hardware-revision-dialog.component';

@Component({
    selector: 'app-sample-hardware',
    imports: [
        PageHeaderComponent,
        IconField,
        InputIcon,
        FormsModule,
        TableModule,
        Button,
        SharedModule,
        CreateSampleHardwareRevisionDialogComponent,
        UpdateSampleHardwareRevisionDialogComponent,
        InputText,
        IftaLabelModule,
        IsAdminDirective,
        Tooltip,
    ],
    templateUrl: './sample-hardware.component.html',
    styleUrl: './sample-hardware.component.scss'
})
export class SampleHardwareComponent {
    public search = '';
    public sampleHardwareRevisions: SampleHardwareRevision[] = [];
    public totalRecords = 0;
    public rows = 10;
    public currentPage = 0;
    public loading = false;
    public displayCreateSampleHardwareRevisionDialog = false;
    public displayUpdateSampleHardwareRevisionDialog = false;
    public selectedSampleHardwareRevision: SampleHardwareRevisionWriteDTO = new SampleHardwareRevisionWriteDTO({});

    private searchSubject = new Subject<string>();

    constructor(private sampleHardwareRevisionService: SampleHardwareRevisionService,
                private messageService: MessageService,
                private confirmationService: ConfirmationService) {
    }

    /**
     * Initializes the component and loads the sample types.
     */
    public ngOnInit(): void {
        this.loadSampleHardwareRevisions();
        this.searchSubject.pipe(debounceTime(300)).subscribe(() => {
            this.currentPage = 0;
            this.loadSampleHardwareRevisions();
        });
    }

    /**
     * Loads the sample types from the server.
     */
    public loadSampleHardwareRevisions(): void {
        this.loading = true;
        const size = this.rows;
        const search = this.search || '';

        this.sampleHardwareRevisionService
            .getSampleHardwareRevisions(search, this.currentPage, size)
            .then((result: Page<SampleHardwareRevision>) => {
                this.sampleHardwareRevisions = result.content;
                this.totalRecords = result.page.totalElements;
            })
            .catch((error: any) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
            })
            .finally(() => {
                this.loading = false;
            });
    }

    /**
     * Handles the page change event.
     *
     * @param event The page change event.
     */
    public onPageChange(event: any): void {
        this.currentPage = Math.floor(event.first / event.rows);
        this.rows = event.rows;
        this.loadSampleHardwareRevisions();
    }

    /**
     * Handles the search input change event.
     */
    public onSearchInputChange(): void {
        this.searchSubject.next(this.search);
    }

    /**
     * Opens the edit sample type dialog.
     */
    public editSampleHardwareRevision(sampleHardwareRevision: SampleHardwareRevision): void {
        this.selectedSampleHardwareRevision = new SampleHardwareRevisionWriteDTO(sampleHardwareRevision);
        this.displayUpdateSampleHardwareRevisionDialog = true;
    }

    /**
     * Deletes a sample type after user confirmation.
     *
     * @param sampleHardwareRevision The sample type to delete.
     */
    public deleteSampleHardwareRevision(sampleHardwareRevision: SampleHardwareRevision): void {
        this.confirmationService.confirm({
            message: 'Are you sure you want to delete the sample hardware revision ' +
                sampleHardwareRevision.pcbVersion + '?',
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.sampleHardwareRevisionService.deleteSampleHardwareRevision(sampleHardwareRevision.id).then(() => {
                    this.loadSampleHardwareRevisions();
                    this.messageService.add(
                        {
                            severity: 'success',
                            summary: 'Success',
                            detail: 'Sample hardware revision deleted',
                            life: 3000
                        });
                }).catch((error) => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
                });
            }
        });
    }

    /**
     * Opens the dialog to create a new sample type.
     */
    public openCreateSampleHardwareRevisionDialog(): void {
        this.displayCreateSampleHardwareRevisionDialog = true;
    }

    /**
     * Reloads the sample types.
     */
    public reloadSampleHardwareRevisions(): void {
        this.loadSampleHardwareRevisions();
    }
}
